import { useMutation } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { ApiError, apiFetch } from '../api';
import { useMe } from '../auth';
import { m } from '../paraglide/messages';
import { rootRoute } from './root';

// Magic-link redemption lands here after Better Auth's /auth/magic-link/verify
// has set the session cookie and 302'd to this URL. Two flavours:
//
//   /auth/callback                         — regular sign-in. Re-fetch /me,
//                                            navigate to /cases.
//   /auth/callback?diagnostic=TOKEN        — diagnostic handoff from landing.
//                                            Once authenticated, POST the
//                                            token to /cases/from-diagnostic
//                                            so the api can claim the lead
//                                            and seed a case with the funnel
//                                            answers, then navigate straight
//                                            to that case.
//
// `?error=...` short-circuits both paths and shows a recovery card.

type ClaimResponse = { case: { id: string } };

type ClaimErrorCode =
  | 'lead_not-found'
  | 'lead_expired'
  | 'lead_already-claimed'
  | 'lead_email-mismatch'
  | 'bad_request'
  | string;

export const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallbackRoute,
  validateSearch: (s: Record<string, unknown>) => ({
    error: typeof s.error === 'string' ? s.error : undefined,
    diagnostic: typeof s.diagnostic === 'string' ? s.diagnostic : undefined,
  }),
});

function AuthCallbackRoute() {
  const me = useMe();
  const navigate = useNavigate();
  const { error, diagnostic } = authCallbackRoute.useSearch();
  const [claimError, setClaimError] = useState<ClaimErrorCode | null>(null);

  const claim = useMutation({
    mutationFn: (token: string) =>
      apiFetch<ClaimResponse>('/cases/from-diagnostic', {
        method: 'POST',
        body: { token },
      }),
  });

  // Once-only guard: in StrictMode the effect fires twice; we only want one
  // claim attempt per page load.
  const claimedRef = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: claim.mutate is stable across renders; including `claim` would loop
  useEffect(() => {
    if (error) return;
    if (me.isLoading) return;
    if (!me.data?.user) {
      // No session yet — Better Auth's redirect still in flight, or the link
      // was bad. If we're definitely not fetching, fall back to /login.
      if (!me.isFetching) navigate({ to: '/login', replace: true });
      return;
    }

    // Authenticated. If a diagnostic token rode along, claim it and route
    // straight to the seeded case. Otherwise just go to /cases.
    if (!diagnostic) {
      navigate({ to: '/cases', replace: true });
      return;
    }
    if (claimedRef.current) return;
    claimedRef.current = true;

    claim.mutate(diagnostic, {
      onSuccess: (data) => {
        navigate({
          to: '/cases/$id',
          params: { id: data.case.id },
          search: { log: undefined },
          replace: true,
        });
      },
      onError: (err) => {
        const code: ClaimErrorCode =
          err instanceof ApiError && typeof err.body === 'object' && err.body !== null
            ? ((err.body as { code?: string }).code ?? 'unknown')
            : 'unknown';
        setClaimError(code);
      },
    });
  }, [error, me.isLoading, me.isFetching, me.data, diagnostic, navigate]);

  if (error) {
    return (
      <div className="card narrow">
        <h1>{m.authCallbackErrorHeading()}</h1>
        <p className="muted">
          Code: <code>{error}</code>
        </p>
        <p>{m.authCallbackErrorBody()}</p>
      </div>
    );
  }

  if (claimError) {
    return (
      <div className="card narrow">
        <h1>{m.authCallbackClaimErrorHeading()}</h1>
        <p className="muted">
          Code: <code>{claimError}</code>
        </p>
        <p>
          {claimError === 'lead_email-mismatch'
            ? m.authCallbackEmailMismatch()
            : claimError === 'lead_expired'
              ? m.authCallbackExpired()
              : claimError === 'lead_already-claimed'
                ? m.authCallbackAlreadyClaimed()
                : m.authCallbackUnknownError()}
        </p>
        <button type="button" onClick={() => navigate({ to: '/cases', replace: true })}>
          {m.authCallbackContinueButton()}
        </button>
      </div>
    );
  }

  return <p className="centered muted">{m.authCallbackSigningIn()}</p>;
}
